import { Fab, IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Menu, MenuItem } from "@material-ui/core";
import { Add, Delete, FileCopy, MoreHoriz } from "@material-ui/icons";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { useSnackbar } from "notistack";
import React, { useCallback, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";

import { Collections } from "Collections";
import { useAppUser } from "components/AppUserProvider";
import { useOrgUser } from "components/OrgUserProvider";
import { PageNavigation } from "components/PageNavigation";
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { DbDoc, WithRef } from "models/FbDoc";
import { Nullable } from "models/Nullable";
import { Paths } from "Paths";
import { isEmpty } from "Util/isEmpty";
import { searchStringFrom } from "Util/searchStringFrom";
import { sortAlphabetically } from "Util/sortAlphabetically";

type Props<T> = {
    aboveList?: React.ReactNode
    topItems?: React.ReactNode | ((entries?: WithRef<DbDoc<T>>[]) => React.ReactNode)
    title: string
    collectionName: Collections
    noEntriesText: string
    createNewText?: string
    fieldName: string
    path?: Paths
    createPath?: Paths
    onCopy?: (oldDocId: string, newDocId: string) => Promise<any>
    getSecondaryText?: (entry: T) => React.ReactNode
    getActions?: (entry: WithRef<DbDoc<T>>) => React.ReactNode
    getQuery?: FirebaseUtils.QueryGetter<T>
    standardBackButton?: boolean
    loadingQueryData?: boolean
    persist?: boolean
    onClickEntry?: (entry: T) => void
    getAvatar?: (entry: T) => React.ReactNode
    getName: (entry: T) => Nullable<string> | undefined
    getNamePrefix?: (entry: T) => React.ReactNode
    getErrorEvent: AnalyticsEvent
    copyProps?: {
        copyEvent: AnalyticsEvent,
        copyContextEvent: AnalyticsEvent,
        copyErrorEvent: AnalyticsEvent,
    }
    deleteProps?: {
        deleteErrorEvent: AnalyticsEvent
        deleteEvent: AnalyticsEvent
        deleteContextEvent: AnalyticsEvent
    }
    canCreate?: boolean
    canDelete?: boolean
    dense?: boolean
}

export type Entry = {
    id: string
}

type MenuButtonProps = {
    label: string
    icon: JSX.Element
    options: Nullable<[label: string, icon: JSX.Element, onClick: () => void]>[]
    color?: "primary" | "secondary"
}

export function MenuButton({ label, icon, options, color }: MenuButtonProps) {
    if (!options.some(option => option !== null)) {
        return null
    }
    return (
        <PopupState variant="popover" popupId="demo-popup-menu">
            {(popupState) => (
                <React.Fragment>
                    <IconButton size="small" aria-label={label} color={color} {...bindTrigger(popupState)}>
                        {icon}
                    </IconButton>
                    <Menu {...bindMenu(popupState)}>
                        {options.map((option) => {
                            if (!option) {
                                return null
                            }
                            const [optionLabel, optionIcon, optionOnClick] = option
                            return (
                                <MenuItem key={optionLabel} onClick={() => {
                                    popupState.close()
                                    optionOnClick()
                                }}>
                                    <ListItemIcon>
                                        {optionIcon}
                                    </ListItemIcon>
                                    <ListItemText primary={optionLabel} />
                                </MenuItem>
                            )
                        })}
                    </Menu>
                </React.Fragment>
            )}
        </PopupState>
    )
}

export function useFilterByName<T>(search: Nullable<string> | undefined, entries: Nullable<T[]> | undefined, getValue: (entry: T) => Nullable<string> | undefined): Nullable<T[]> | undefined {
    return useMemo(() => {
        if (isEmpty(search)) {
            return entries
        }
        const searchString = searchStringFrom(search)
        return entries?.filter((entry) => {
            const value = getValue(entry)
            return !!searchStringFrom(value === undefined ? null : value)?.includes(searchString)
        })
    }, [entries, search, getValue])

}

export function ListScreen<T extends Entry>({
    title,
    collectionName,
    noEntriesText,
    createNewText,
    fieldName,
    path,
    createPath,
    getSecondaryText,
    onCopy,
    copyProps,
    deleteProps,
    getActions,
    getQuery,
    standardBackButton,
    loadingQueryData,
    persist,
    getAvatar,
    getName,
    getNamePrefix,
    getErrorEvent,
    canCreate: userCanCreate,
    canDelete,
    onClickEntry,
    dense,
    aboveList,
    topItems,
}: Props<T>) {
    const { enqueueSnackbar } = useSnackbar()
    const history = useHistory();
    const [entries, loadingEntries] = FirebaseUtils.useCollectionData<T>(
        !!persist,
        collectionName,
        getErrorEvent,
        {},
        (query, path) => getQuery ? getQuery(query, path) : query,
    )

    const [appUserDoc, loadingAppUser] = useAppUser()
    const [orgUserDoc, loadingOrgUser] = useOrgUser()
    const [search, setSearch] = useState('')

    const appUser = appUserDoc?.data
    const orgUser = orgUserDoc?.data

    const canCreate = !!createNewText && !!createPath && userCanCreate

    const loading = loadingEntries || loadingQueryData || loadingAppUser || loadingOrgUser

    const sorted = useMemo(() => {
        return entries?.sort((a, b) => sortAlphabetically(getName(a.data) || '', getName(b.data) || ''))
    }, [entries, getName])

    const filtered = useFilterByName(search, sorted, useCallback((entry: WithRef<DbDoc<T>>) => getName(entry.data), [getName]))

    return (
        <PageNavigation title={title} loading={loading} backHistoryButton={standardBackButton} loadingScreenId={title} search={search} setSearch={setSearch}>
            {aboveList}
            {(canCreate && (
                <Fab
                    color="secondary"
                    aria-label={"add " + fieldName}
                    style={{ position: "fixed", bottom: 20, right: 20, zIndex: 1050 }}
                    component={Link}
                    to={createPath || ''}
                >
                    <Add />
                </Fab>
            ))}
            <List dense={!!dense}>
                {topItems && ((typeof topItems === 'function') ? topItems(entries) : topItems)}
                {(!loading && !entries?.length) ? (
                    (canCreate ? (
                        <ListItem component={Link} to={createPath || ''}>
                            <ListItemIcon>
                                <Add />
                            </ListItemIcon>
                            <ListItemText primary={noEntriesText} secondary={createNewText} />
                        </ListItem>
                    ) : (
                        <ListItem>
                            <ListItemText primary={noEntriesText} />
                        </ListItem>
                    ))
                ) : null}
                {(filtered?.length) ? (
                    <>
                        {(
                            filtered.map((entryDoc) => {
                                const { data: entry } = entryDoc
                                return (
                                    <ListItem
                                        key={entry.id}
                                        button
                                        divider
                                        {...(path ? {
                                            component: Link,
                                            to: path + '/' + entry.id,
                                        } : {})}
                                        onClick={() => onClickEntry?.(entry)}
                                    >
                                        {getAvatar?.(entry)}
                                        <ListItemText primary={<>
                                            {getNamePrefix?.(entry)}
                                            {getName(entry)}
                                        </>}
                                            secondary={getSecondaryText?.(entry)} />
                                        <ListItemSecondaryAction>
                                            <MenuButton
                                                label="Alternativer"
                                                icon={<MoreHoriz />}

                                                options={[
                                                    (!copyProps || !orgUser || !appUser || !canCreate) ? null : ['Kopier', <FileCopy />, async () => {
                                                        const { id, ...rest } = entry;
                                                        const {
                                                            copyEvent, copyContextEvent, copyErrorEvent,
                                                        } = copyProps;
                                                        const newId = FirebaseUtils.createDocument<T>(
                                                            true,
                                                            collectionName,
                                                            null,
                                                            appUser,
                                                            orgUser,
                                                            copyEvent,
                                                            copyContextEvent,
                                                            copyErrorEvent,
                                                            enqueueSnackbar,
                                                            'Kopiert!',
                                                            rest
                                                        );

                                                        await onCopy?.(entry.id, newId);
                                                        history.push(path + '/' + newId);
                                                    }],
                                                    (!deleteProps || !appUser || !canDelete) ? null : ["Slett", <Delete />, () => {
                                                        FirebaseUtils.deleteDocument(
                                                            true,
                                                            collectionName,
                                                            entry.id,
                                                            appUser,
                                                            orgUser || null,
                                                            deleteProps.deleteEvent,
                                                            deleteProps.deleteContextEvent,
                                                            deleteProps.deleteErrorEvent,
                                                            enqueueSnackbar,
                                                            'Slettet!',
                                                            () => ({ appUserId: appUser.id, })
                                                        );
                                                    }]
                                                ]} />
                                            {getActions?.(entryDoc)}
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                );
                            })
                        )}
                    </>
                ) : (sorted?.length ? (
                    <ListItem>
                        <ListItemText primary="Ingen som passer med søket" />
                    </ListItem>
                ) : null)}
            </List>
        </PageNavigation >
    )
}